import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ShoppingCart, Heart, User, Menu, X, MapPin, ChevronDown, Sparkles, Camera,
  Cpu, Shirt, Sofa, ShoppingBasket, Wrench, Car, Palmtree, Briefcase, Handshake, LogOut, LayoutDashboard, Globe,
  Cigarette, GlassWater, Palette, Footprints, Gem, ShoppingBag, Anchor, Sailboat, PawPrint, Lock,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGS } from "@/context/LanguageContext";
import { LanguagePicker } from "@/components/LanguagePicker";
import { api } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/format";
import { COUNTRIES } from "@/lib/countries";
import { SearchSuggest } from "@/pages/SearchPage";
import { VoiceSearch } from "@/components/VoiceSearch";

const ICONS = { Cpu, Shirt, Sofa, ShoppingBasket, Wrench, Car, Palmtree, Briefcase, Handshake, Sparkles,
  Cigarette, GlassWater, Palette, Footprints, Gem, ShoppingBag, Anchor, Sailboat, PawPrint, Lock };

const SECONDARY = [
  { label: "Categories", to: "#mega" },
  { label: "Deals", to: "/deals" },
  { label: "Jamaica Made", to: "/jamaica-made" },
  { label: "SHOP+", to: "/shop-plus" },
  { label: "Travel & Stays", to: "/travel" },
  { label: "Delivery & Logistics", to: "/delivery" },
  { label: "🌍 EXPORT", to: "/export" },
  { label: "Electronics", to: "/category/electronics" },
  { label: "Fashion", to: "/category/fashion" },
  { label: "Grocery", to: "/grocery" },
  { label: "Books", to: "/books" },
  { label: "Hardware", to: "/hardware" },
  { label: "Paint", to: "/paint" },
  { label: "Steel", to: "/materials/steel" },
  { label: "Cement", to: "/materials/cement" },
  { label: "Roofing", to: "/materials/roofing" },
  { label: "Tiles", to: "/materials/tiles" },
  { label: "Doors", to: "/materials/doors" },
  { label: "Pallet Loader", to: "/pallet-loader" },
  { label: "Order by List", to: "/ai-order" },
  { label: "Vehicles", to: "/automotive" },
  { label: "Food & Dining", to: "/food" },
  { label: "Cuba Collection", to: "/cuba" },
  { label: "Smart AI", to: "#smart-ai", smart: true },
  { label: "Smart Locks", to: "/category/smart-locks" },
  { label: "Business", to: "/category/business" },
  { label: "Services", to: "/services" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-home">
      <img src="/shopnow-logo.png" alt="SHOP NOW" className="h-11 w-11 object-contain" />
      <div className="leading-none">
        <div className="font-clash font-bold text-lg tracking-tight text-ink">SHOP NOW</div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-emerald font-semibold">Global</div>
      </div>
    </Link>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { count, setDrawerOpen, wishlist } = useCart();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const [q, setQ] = useState("");
  const [parish, setParish] = useState(localStorage.getItem("sn_parish") || "Kingston");
  const [parishes, setParishes] = useState([]);
  const [mega, setMega] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [cats, setCats] = useState([]);
  const [acct, setAcct] = useState(false);
  const megaRef = useRef();
  const deskRef = useRef();
  const mobRef = useRef();

  useEffect(() => {
    api.get("/meta").then(({ data }) => { setParishes(data.parishes); }).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem("sn_parish", parish); }, [parish]);

  const MAT_CAT = { steel: "Structural Steel", cement: "Cement", roofing: "Roofing", tiles: "Tiles", lumber: "Lumber", doors: "Doors" };
  const submitSearch = (e) => {
    e.preventDefault();
    const m = window.location.pathname.match(/^\/materials\/([a-z]+)/);
    const cat = m && MAT_CAT[m[1]] ? `&category=${encodeURIComponent(MAT_CAT[m[1]])}` : "";
    navigate(`/search?q=${encodeURIComponent(q)}${cat}`);
    setMobileNav(false);
  };

  const dashLink = user && (["admin", "founder", "accountant", "auditor", "support"].includes(user.role) ? "/admin" : user.role === "supplier" ? "/supplier" : user.restaurant_slug ? "/restaurant" : user.role === "merchant" ? "/seller" : user.role === "driver" ? "/driver" : "/account");

  return (
    <header className="sticky top-0 z-50 overflow-x-clip">
      <div className="bg-ink text-white/70 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 flex items-center justify-between overflow-x-auto sn-scroll-x">
          <span className="hidden sm:block shrink-0" data-testid="topbar-tagline">Fastest delivery service worldwide 🚚 ✈️ 🛳️ 🚀</span>
          <div className="flex items-center gap-4 whitespace-nowrap shrink-0">
            <Link to="/seller" className="hover:text-white">{t("topbar_sell")}</Link>
            <Link to="/community" className="hover:text-white">{t("topbar_community")}</Link>
            <Link to="/careers" className="hover:text-white">{t("topbar_careers")}</Link>
            <Link to="/driver" className="hover:text-white">{t("topbar_driver")}</Link>
            <Link to="/support" className="hover:text-white">{t("topbar_help")}</Link>
            <Link to="/pay" className="hover:text-white" data-testid="topbar-pay-bill">Pay a Bill</Link>
            <LanguagePicker testid="language-switcher" />
          </div>
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3 sm:gap-5">
          <button className="lg:hidden" onClick={() => setMobileNav(true)} data-testid="mobile-nav-open"><Menu /></button>
          <Logo />
          <div className="hidden md:block"><VoiceSearch /></div>
          <div className="md:hidden ml-auto"><VoiceSearch compact /></div>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-2xl items-center relative">
            <div className="flex w-full items-center rounded-full border border-ink/15 bg-secondary/60 focus-within:border-emerald focus-within:ring-2 focus-within:ring-emerald/20 transition">
              <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} ref={deskRef} autoComplete="off"
                placeholder={t("search_ph")}
                className="flex-1 bg-transparent px-4 py-2.5 text-sm outline-none" />
              <Link to="/snap" title="Snap a photo to find a product" className="m-1 h-9 w-9 grid place-items-center rounded-full text-ink/70 hover:bg-ink/10 transition-colors" data-testid="header-snap-link"><Camera size={17} /></Link>
              <button type="submit" className="m-1 h-9 w-11 grid place-items-center rounded-full bg-ink text-white hover:bg-emerald-deep transition-colors">
                <Search size={17} />
              </button>
            </div>
            <SearchSuggest q={q} inputRef={deskRef} onPick={setQ} />
          </form>

          <div className="hidden lg:flex items-center gap-1 text-sm">
            <MapPin size={16} className="text-emerald" />
            <select value={parish} onChange={(e) => setParish(e.target.value)}
              className="bg-transparent font-semibold outline-none cursor-pointer max-w-[160px]" data-testid="parish-selector" title="Deliver to">
              <optgroup label="🇯🇲 Jamaica — Parishes">
                {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
              </optgroup>
              <optgroup label="🌍 Worldwide">
                {COUNTRIES.filter((c) => c !== "Jamaica").map((c) => <option key={c} value={c}>{c}</option>)}
              </optgroup>
            </select>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 md:ml-auto lg:ml-0">
            <div className="relative">
              <button onClick={() => (user ? setAcct((v) => !v) : navigate("/login"))}
                className="flex items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-secondary transition-colors" data-testid="account-btn">
                <User size={20} />
                <span className="hidden xl:block text-sm font-medium">{user ? user.name.split(" ")[0] : t("account")}</span>
                {user && <ChevronDown size={14} />}
              </button>
              {user && acct && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-border shadow-xl p-1.5 z-50" onMouseLeave={() => setAcct(false)}>
                  <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border mb-1">
                    {t("signed_in_as")} <span className="font-semibold text-ink capitalize">{user.role}</span>
                  </div>
                  <Link to={dashLink} onClick={() => setAcct(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary text-sm" data-testid="dashboard-link">
                    <LayoutDashboard size={16} /> {t("dashboard")}
                  </Link>
                  <Link to="/account" onClick={() => setAcct(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary text-sm">
                    <User size={16} /> {t("my_account")}
                  </Link>
                  <button onClick={() => { setAcct(false); logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary text-sm text-coral" data-testid="logout-btn">
                    <LogOut size={16} /> {t("sign_out")}
                  </button>
                </div>
              )}
            </div>

            <Link to="/account" className="hidden sm:flex items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-secondary relative" data-testid="wishlist-link">
              <Heart size={20} />
              {wishlist.length > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 grid place-items-center text-[10px] font-bold bg-coral text-white rounded-full">{wishlist.length}</span>}
            </Link>

            <button onClick={() => setDrawerOpen(true)} data-testid="cart-drawer-trigger"
              className="flex items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-secondary relative">
              <ShoppingCart size={20} />
              {count > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 grid place-items-center text-[10px] font-bold bg-emerald text-white rounded-full">{count}</span>}
            </button>
          </div>
        </div>

        <form onSubmit={submitSearch} className="md:hidden px-4 pb-3 relative">
          <div className="flex items-center rounded-full border border-ink/15 bg-secondary/60">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search everything…" ref={mobRef} autoComplete="off"
              className="flex-1 bg-transparent px-4 py-2.5 text-sm outline-none" data-testid="mobile-search-input" />
            <Link to="/snap" className="m-1 h-9 w-9 grid place-items-center rounded-full text-ink/70" data-testid="mobile-snap-link"><Camera size={17} /></Link>
            <button className="m-1 h-9 w-11 grid place-items-center rounded-full bg-ink text-white"><Search size={17} /></button>
          </div>
          <div className="absolute left-4 right-4 top-full -mt-2"><SearchSuggest q={q} inputRef={mobRef} onPick={setQ} mobile /></div>
        </form>

        <div className="border-t border-border relative" onMouseLeave={() => setMega(false)}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center gap-6 overflow-x-auto sn-scroll-x">
            <button data-testid="category-megamenu-button" onMouseEnter={() => setMega(true)} onClick={() => setMega((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold text-ink shrink-0">
              <Menu size={16} /> {t("all_categories")} <ChevronDown size={14} />
            </button>
            {SECONDARY.slice(1).map((s) => s.smart ? (
              <button key={s.label} onClick={() => window.dispatchEvent(new CustomEvent("shopai:open"))} data-testid="smart-ai-nav-btn" className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-ink hover:bg-[#E11B22] rounded-full px-3 py-1 whitespace-nowrap shrink-0 transition-colors">
                <Sparkles size={14} /> {s.label}
              </button>
            ) : (
              <Link key={s.label} to={s.to} className="text-sm text-slate-600 hover:text-emerald whitespace-nowrap sn-underline-grow shrink-0">
                {s.label}
              </Link>
            ))}
          </div>

          {mega && (
            <div ref={megaRef} className="absolute left-0 right-0 top-full bg-white border-b border-border shadow-2xl sn-fade-up hidden lg:block">
              <div className="max-w-7xl mx-auto px-8 py-6 grid grid-cols-5 gap-4">
                {cats.map((c) => {
                  const Ic = ICONS[c.icon] || Sparkles;
                  const subs = c.subcategories || [];
                  return (
                    <div key={c.slug} className="p-3 rounded-xl hover:bg-secondary transition-colors" data-testid={`mega-cat-${c.slug}`}>
                      <Link to={`/category/${c.slug}`} onClick={() => setMega(false)} className="group flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="h-9 w-9 grid place-items-center rounded-lg bg-emerald/10 text-emerald group-hover:bg-emerald group-hover:text-white transition-colors">
                            <Ic size={18} />
                          </span>
                          <span className="font-display font-semibold text-sm">{c.name}</span>
                        </div>
                        <span className="text-[11px] text-emerald font-semibold">{c.count} products</span>
                      </Link>
                      {subs.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`mega-subs-${c.slug}`}>
                          {subs.map((sc) => (
                            <Link key={sc.slug} to={`/category/${c.slug}?sub=${sc.slug}`} onClick={() => setMega(false)}
                              className="text-[11px] px-2 py-1 rounded-full bg-white border border-border text-slate-600 hover:border-emerald hover:text-emerald transition-colors">
                              {sc.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      {mobileNav && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85%] bg-white p-5 overflow-y-auto sn-fade-up">
            <div className="flex items-center justify-between mb-5"><Logo /><button onClick={() => setMobileNav(false)}><X /></button></div>
            <div className="mb-4 space-y-2">
              <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5">
                <Globe size={16} className="text-emerald shrink-0" />
                <LanguagePicker testid="language-switcher-mobile" dark={false} />
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5">
                <MapPin size={16} className="text-emerald shrink-0" />
                <select value={parish} onChange={(e) => setParish(e.target.value)} data-testid="parish-selector-mobile"
                  className="flex-1 bg-transparent text-sm outline-none font-medium" aria-label="Deliver to">
                  <optgroup label="🇯🇲 Jamaica — Parishes">
                    {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
                  </optgroup>
                  <optgroup label="🌍 Worldwide">
                    {COUNTRIES.filter((c) => c !== "Jamaica").map((c) => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              {cats.map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} onClick={() => setMobileNav(false)}
                  className="block px-3 py-2.5 rounded-lg hover:bg-secondary font-medium">{c.name}</Link>
              ))}
              <div className="h-px bg-border my-3" />
              {SECONDARY.slice(1).map((s) => (
                <Link key={s.label} to={s.to} onClick={() => setMobileNav(false)} className="block px-3 py-2.5 rounded-lg hover:bg-secondary">{s.label}</Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
